import {api} from '@nfq/typed-next-api';

import type {HTTP_METHODS, MutationRepoArgs} from '@nfq/typed-next-api';
import type {saveApplication} from 'ApiRoutes/application';
import type {ApplicationFormServiceAdapter} from 'Domain/adapters/ApplicationFormServiceAdapter';
import type {ApplicationFormData} from 'Domain/entities/ApplicationForm';

/**
 * The `ApplicationFormService` class provides functionality for handling application form submissions.
 * This service implements the ApplicationFormServiceAdapter interface and manages the conversion of form data
 * to FormData format before sending it to the server API. It handles both file uploads and regular form fields.
 */
class ApplicationFormService implements ApplicationFormServiceAdapter {
    /**
     * Sends an application form to the server by converting the form data to FormData format and making an API request.
     * This method processes both file uploads and regular form fields, appending files to a 'files' field
     * and converting other values to strings before submission.
     *
     * @param key              The unique identifier for the mutation request.
     * @param props            The mutation repository arguments containing the request data.
     * @param props.arg        The argument object containing the request body and HTTP method.
     * @param props.arg.body   The application form data to be sent to the server.
     * @param props.arg.method The HTTP method to use for the request.
     * @returns A Promise that resolves when the application form has been successfully sent.
     *
     * @example
     * ```tsx
     * const service = new ApplicationFormService();
     * await service.sendApplicationForm('unique-key', {
     *     arg: {
     *         body: {
     *             firstName: 'John',
     *             lastName: 'Doe',
     *             resume: fileObject
     *         },
     *         method: HTTP_METHODS.POST
     *     }
     * });
     * ```
     */
    async sendApplicationForm(
        key: string,
        {arg: {body, method}}: MutationRepoArgs<ApplicationFormData, HTTP_METHODS.POST>
    ) {
        const formData = new FormData();

        Object.entries(body).forEach(([field, value]) => {
            if (value instanceof File) {
                formData.append('files', value);
            } else {
                formData.append(field, String(value));
            }
        });

        await api<typeof saveApplication>('/api/application', {
            body: formData,
            method
        });
    }
}

export const applicationFormService = new ApplicationFormService();